import React from 'react';
import { motion } from 'framer-motion';
import { Package, Clock, MapPin, AlertTriangle, Clipboard, Loader, CheckCircle, XCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import type { PickList } from '../../types';

interface PickListCardProps {
  pickList: PickList;
  onClick: (pickList: PickList) => void;
}

const getPriorityDot = (priority: string) => {
  switch (priority) {
    case 'high':
    case 'urgent':
      return <div className="w-3 h-3 bg-red-500 rounded-full" title="High Priority" />;
    case 'medium':
      return <div className="w-3 h-3 bg-orange-500 rounded-full" title="Medium Priority" />;
    case 'low':
    default:
      return <div className="w-3 h-3 bg-gray-400 rounded-full" title="Low Priority" />;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
    case 'assigned':
      return <Clipboard className="w-4 h-4 text-saavi-600" title="Pending" />;
    case 'in_progress':
      return <Loader className="w-4 h-4 text-orange-500" title="In Progress" />;
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-white" title="Completed" />;
    case 'cancelled':
      return <XCircle className="w-4 h-4 text-red-500" title="Cancelled" />;
    default:
      return <Clipboard className="w-4 h-4 text-saavi-600" title="Pending" />;
  }
};

export const PickListCard: React.FC<PickListCardProps> = ({ pickList, onClick }) => {
  const progress = pickList.totalItems > 0 ? (pickList.pickedItems / pickList.totalItems) * 100 : 0;
  const isOverdue = pickList.dueDate && new Date(pickList.dueDate) < new Date();

  return (
    <Card hover onClick={() => onClick(pickList)} className="relative">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-saavi-100 rounded-lg flex items-center justify-center">
            <Package className="w-6 h-6 text-saavi-600" />
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900">{pickList.customerName}</h3>
            <p className="text-sm text-gray-600">{pickList.orderNumber}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {getPriorityDot(pickList.priority)}
          {getStatusIcon(pickList.status)}
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Progress</span>
          <span>{pickList.pickedItems}/{pickList.totalItems} items</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            className="bg-blue-600 h-2 rounded-full"
          />
        </div>
      </div>
      
      {/* Metadata */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center space-x-4">
          {pickList.estimatedTime && (
            <div>
              <span>{pickList.totalItems} locations</span>
            </div>
          )}
          
          <div className="flex items-center space-x-1">
            <MapPin className="w-4 h-4 text-saavi-600" />
            <span>{pickList.totalItems} locations</span>
          </div>
        </div>
        
        {isOverdue && (
          <div className="flex items-center space-x-1 text-red-600">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="font-medium">Overdue</span>
          </div>
        )}
      </div>
      
      {/* Overdue indicator */}
      {isOverdue && (
        <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
      )}
    </Card>
  );
};